/**
 * Module dependencies.
 */

import { apiEndpoints } from 'app/core/config/api-endpoints';
import { appRoutes } from 'app/routes';
import { identity, omit, pickBy } from 'lodash';
import {
  CrudAddType,
  CrudEditType,
  CrudListType,
  CrudTemplate,
  FormList,
  ListColumns
} from '../../../organisms/crud/form-types';
import { TFunction } from 'react-i18next';
import { ListFilters } from 'app/components/organisms/crud/form-filters/form-filters-type';
import { DateTime } from 'app/components/atoms/date-time/date-time';
import { Country } from 'app/components/atoms/country/country';
import { useFetchVersions } from 'app/hooks/app-requests/translations/use-fetch-versions';
import { useGetLanguages } from 'app/hooks/app-requests/use-get-languages';
import React from 'react';

/**
 * `Translation` type.
 */

type Translation = {
  id: string;
  translationKey: string;
  version: string;
  languageCode: string;
  value: string;
  createdAt: string;
  updatedAt: string;
};

/**
 * List labels.
 */

const listColumns = (translate: TFunction): ListColumns<Translation> => [
  {
    title: translate('common.labels.translationKey'),
    dataIndex: 'translationKey',
    size: '1fr',
    key: 'translationKey',
    render: item => <span style={{ fontFamily: 'monospace', fontSize: '13px' }}>{item.translationKey}</span>
  },
  {
    title: translate('common.labels.version'),
    dataIndex: 'version',
    key: 'version',
    render: item => <span style={{ fontSize: '13px' }}>{item.version}</span>
  },
  {
    title: translate('common.labels.languageCode'),
    dataIndex: 'languageCode',
    key: 'languageCode',
    render: item => <Country code={item.languageCode} extraPadding />
  },
  {
    title: translate('common.labels.value'),
    dataIndex: 'value',
    key: 'value',
    render: item => (
      <span style={{ fontSize: '12px' }}>
        {item.value.length > 100 ? `${item.value.substring(0, 100)}...` : item.value}
      </span>
    )
  },
  {
    title: translate('common.labels.createdAt'),
    dataIndex: 'createdAt',
    key: 'createdAt',
    render: value => <DateTime date={value.createdAt} showMilliseconds />
  },
  {
    title: translate('common.labels.updatedAt'),
    dataIndex: 'updatedAt',
    key: 'updatedAt',
    render: value => <DateTime date={value.updatedAt} showMilliseconds />
  }
];

/**
 * List filters.
 */

const listFilters = (translate: TFunction): ListFilters => [
  {
    type: 'inputSearch',
    inline: true,
    name: 'search',
    label: translate('common.labels.search'),
    style: { flexBasis: '100%', marginBottom: '8px' }
  },
  {
    type: 'selectSearch',
    name: 'version',
    inline: true,
    label: translate('common.labels.version'),
    showSearch: true,
    allowClear: true,
    options: {
      hook: useFetchVersions,
      normalize: (data: any) => {
        // data should be an array of version strings directly
        const versions = Array.isArray(data) ? data : [];
        return versions.map((version: string) => ({
          label: version,
          value: version,
          searchValue: version
        }));
      }
    }
  },
  {
    type: 'selectSearch',
    name: 'languageCode',
    inline: true,
    label: translate('common.labels.languageCode'),
    showSearch: true,
    allowClear: true,
    options: {
      hook: useGetLanguages,
      normalize: (data: any) => {
        const languages = Array.isArray(data) ? data : [];
        return languages.map((lang: any) => ({
          label: (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Country code={lang.code} noText />
              <span>{JSON.parse(lang.name)?.pt || lang.code}</span>
            </div>
          ),
          value: lang.code,
          searchValue: JSON.parse(lang.name)?.pt || lang.code
        }));
      }
    }
  }
];

/**
 * List.
 */

function list(translate: TFunction): CrudListType<Translation> {
  return {
    columns: listColumns(translate),
    filters: listFilters(translate),
    route: appRoutes.dashboard.developer.translations,
    canEdit: true,
    canRemove: false,
    resolveParams: (params: any, item?: Translation) => {
      return {
        id: params.id ?? item?.id
      };
    },
    key: ['translations'],
    defaultQueryString: 'page=1&pageSize=50',
    normalizeFilters: (data: any) => {
      const { version, languageCode, search, ...restFilters } = data.filters || {};

      return {
        // Filters that go through filterMap (search)
        filters: pickBy({ search }, identity),
        // Direct query params that go through queryMap (version, languageCode)
        ...(version && { version }),
        ...(languageCode && { languageCode }),
        ...pickBy(restFilters, identity),
        order: data.order && data.order.length > 0 ? data.order : [{ key: 'createdAt', direction: 'desc' }]
      };
    },
    normalizeData: (result: any) => {
      return result;
    },
    pagination: {
      pageSize: 50,
      pageSizeOptions: [10, 25, 50, 100, 250],
      pagingByTime: true,
      timeField: 'createdAt'
    },
    endpoint: apiEndpoints.translations,
    endpointById: apiEndpoints.translationsById,
    enableExport: true
  };
}

/**
 * Form fields for add - with dynamic disabled based on values.
 */

const formFieldsAdd = (translate: TFunction): FormList => [
  {
    type: 'inputField',
    name: 'translationKey',
    label: translate('common.labels.translationKey'),
    rules: { required: translate('common.errors.form.required') },
    disabled: (values: any) => !!values?.translationKey
  },
  {
    type: 'selectField',
    name: 'version',
    label: translate('common.labels.version'),
    rules: { required: translate('common.errors.form.required') },
    showSearch: true,
    disabled: (values: any) => !!values?.version,
    options: {
      hook: useFetchVersions,
      normalize: (data: any) => {
        const versions = Array.isArray(data) ? data : [];
        return versions.map((version: string) => ({
          label: version,
          value: version,
          searchValue: version
        }));
      }
    }
  },
  {
    type: 'selectField',
    name: 'languageCode',
    label: translate('common.labels.languageCode'),
    rules: { required: translate('common.errors.form.required') },
    showSearch: true,
    disabled: (values: any) => !!values?.languageCode,
    options: {
      hook: useGetLanguages,
      normalize: (data: any) => {
        const languages = Array.isArray(data) ? data : [];
        return languages.map((lang: any) => ({
          label: (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Country code={lang.code} noText />
              <span>{JSON.parse(lang.name)?.pt || lang.code}</span>
            </div>
          ),
          value: lang.code,
          searchValue: JSON.parse(lang.name)?.pt || lang.code
        }));
      }
    }
  },
  {
    type: 'inputField',
    name: 'value',
    label: translate('common.labels.value'),
    rules: { required: translate('common.errors.form.required') }
  }
];

/**
 * Form fields for edit - always disabled.
 */

const formFieldsEdit = (translate: TFunction): FormList => [
  {
    type: 'inputField',
    name: 'translationKey',
    label: translate('common.labels.translationKey'),
    rules: { required: translate('common.errors.form.required') },
    disabled: true
  },
  {
    type: 'selectField',
    name: 'version',
    label: translate('common.labels.version'),
    rules: { required: translate('common.errors.form.required') },
    showSearch: true,
    disabled: true,
    options: {
      hook: useFetchVersions,
      normalize: (data: any) => {
        const versions = Array.isArray(data) ? data : [];
        return versions.map((version: string) => ({
          label: version,
          value: version,
          searchValue: version
        }));
      }
    }
  },
  {
    type: 'selectField',
    name: 'languageCode',
    label: translate('common.labels.languageCode'),
    rules: { required: translate('common.errors.form.required') },
    showSearch: true,
    disabled: true,
    options: {
      hook: useGetLanguages,
      normalize: (data: any) => {
        const languages = Array.isArray(data) ? data : [];
        return languages.map((lang: any) => ({
          label: (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Country code={lang.code} noText />
              <span>{JSON.parse(lang.name)?.pt || lang.code}</span>
            </div>
          ),
          value: lang.code,
          searchValue: JSON.parse(lang.name)?.pt || lang.code
        }));
      }
    }
  },
  {
    type: 'inputField',
    name: 'value',
    label: translate('common.labels.value'),
    rules: { required: translate('common.errors.form.required') }
  }
];

/**
 * Add.
 */

function add(translate: TFunction): CrudAddType<Translation> {
  return {
    formFields: formFieldsAdd(translate),
    route: `${appRoutes.dashboard.developer.translations}/add`,
    endpoint: apiEndpoints.translations,
    submitLabel: translate('common.actions.add'),
    acceptQueryFields: ['translationKey', 'version', 'languageCode'],
    normalizeInitialValues: (_keys: string[], data: any) => {
      return {
        translationKey: data?.translationKey || '',
        version: data?.version || '',
        languageCode: data?.languageCode || '',
        value: ''
      };
    }
  };
}

/**
 * Edit.
 */

function edit(translate: TFunction): CrudEditType<Translation> {
  return {
    formFields: formFieldsEdit(translate),
    route: `${appRoutes.dashboard.developer.translations}/edit/:id`,
    endpoint: apiEndpoints.translationsById,
    submitLabel: translate('common.actions.edit'),
    normalizePayload: (values: any) => {
      return omit(values, ['id', 'createdAt', 'updatedAt']);
    },
    normalizeInitialValues: (_keys: string[], data: any) => {
      if (!data) {
        return {};
      }

      return {
        translationKey: data.translationKey,
        version: data.version,
        languageCode: data.languageCode,
        value: data.value
      };
    }
  };
}

/**
 * Config.
 */

export function createTranslationsTemplate(translate: TFunction): CrudTemplate<Translation> {
  return {
    list: list(translate),
    add: add(translate),
    edit: edit(translate)
  };
}
